import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Divider,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  AccountBalance as CashIcon,
  Lock as LockIcon,
  LockOpen as OpenIcon,
  Receipt as ReceiptIcon,
  MonetizationOn as MoneyIcon,
  TrendingUp as ProfitIcon,
  Assignment as ReportIcon,
  Print as PrintIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  AttachMoney as DollarIcon,
  CreditCard as CardIcon,
  Payment as PaymentIcon,
  AccountBalanceWallet as WalletIcon,
  Search as SearchIcon,
  History as HistoryIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Visibility as VisibilityIcon,
  ShoppingCart as ShoppingCartIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../utils/currency';
import { PaymentMethod } from '../types';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Link } from 'react-router-dom';
import { useGetLocationsQuery } from '../services/locations.service';
import { useGetUsersQuery } from '../services/users.service';
import {
  useGetActiveSessionQuery,
  useOpenCashDrawerMutation,
  useCloseCashDrawerMutation,
  useCreateAdjustmentMutation,
  useCreateWithdrawalMutation,
  useGetSessionHistoryQuery,
  useGetSessionDetailsQuery,
  CashDrawerSession as APICashDrawerSession,
  CashDrawerTransaction as APICashDrawerTransaction,
} from '../services/cashDrawer.service';

// Use API types
type CashDrawerSession = APICashDrawerSession;
type CashTransaction = APICashDrawerTransaction;

interface CashCount {
  bills_500: number;
  bills_100: number;
  bills_50: number;
  bills_20: number;
  bills_10: number;
  bills_5: number;
  bills_1: number;
  coins_050: number;
  coins_025: number;
  coins_010: number;
  coins_005: number;
}

const CashDrawerPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const userLocationId = user?.locationId;
  const isAdmin = user?.role === 'ADMIN';
  
  // History and filtering state - declared early to avoid hoisting issues
  const [activeTab, setActiveTab] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [historyPage, setHistoryPage] = useState(1);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [showSessionDetailsDialog, setShowSessionDetailsDialog] = useState(false);
  
  // Get locations for display
  const { data: locationsResponse } = useGetLocationsQuery({ active: true });
  const { data: usersResponse } = useGetUsersQuery({ isActive: true, limit: 100 });

  // API hooks
  const { data: sessionResponse, isLoading, error } = useGetActiveSessionQuery(userLocationId || '', {
    skip: !userLocationId,
    refetchOnMountOrArgChange: true,
  });
  
  // History API hook - fetch for selected location or user's location
  const historyLocationId = isAdmin && selectedLocationId ? selectedLocationId : userLocationId;
  const { data: historyResponse, isLoading: isLoadingHistory } = useGetSessionHistoryQuery({
    locationId: historyLocationId || '',
    page: historyPage,
    limit: 10,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    userId: selectedUserId || undefined,
  }, {
    skip: !historyLocationId || activeTab !== 1,
  });

  // Session details API hook - only fetch when dialog is open and sessionId is set
  const { data: sessionDetailsResponse, isLoading: isLoadingSessionDetails } = useGetSessionDetailsQuery(
    selectedSessionId,
    {
      skip: !selectedSessionId || !showSessionDetailsDialog,
    }
  );
  
  const [openCashDrawer, { isLoading: isOpening }] = useOpenCashDrawerMutation();
  const [closeCashDrawer, { isLoading: isClosing }] = useCloseCashDrawerMutation();
  const [createAdjustment, { isLoading: isCreatingAdjustment }] = useCreateAdjustmentMutation();
  const [createWithdrawal, { isLoading: isCreatingWithdrawal }] = useCreateWithdrawalMutation();

  // Local state
  const [openDrawerDialog, setOpenDrawerDialog] = useState(false);
  const [closeDrawerDialog, setCloseDrawerDialog] = useState(false);
  const [adjustmentDialog, setAdjustmentDialog] = useState(false);
  const [withdrawalDialog, setWithdrawalDialog] = useState(false);
  const [openingBalance, setOpeningBalance] = useState<string>('');
  const [cashCount, setCashCount] = useState<CashCount>({
    bills_500: 0,
    bills_100: 0,
    bills_50: 0,
    bills_20: 0,
    bills_10: 0,
    bills_5: 0,
    bills_1: 0,
    coins_050: 0,
    coins_025: 0,
    coins_010: 0,
    coins_005: 0,
  });
  const [adjustmentAmount, setAdjustmentAmount] = useState<string>('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [withdrawalAmount, setWithdrawalAmount] = useState<string>('');
  const [withdrawalReason, setWithdrawalReason] = useState('');
  const [closingStep, setClosingStep] = useState(0);

  const currentSession = sessionResponse?.data || null;
  const locations = locationsResponse?.data || [];
  const users = usersResponse?.users || [];
  const sessionHistory = historyResponse?.data || [];
  const historyPagination = historyResponse?.pagination;
  const sessionDetails = sessionDetailsResponse?.data || null;
  
  // Debug the raw session data
  React.useEffect(() => {
    if (currentSession) {
      console.log('📦 RAW SESSION DATA:', {
        id: currentSession.id,
        openingBalance: currentSession.openingBalance,
        openingBalanceRaw: sessionResponse?.data?.openingBalance,
        transactions: currentSession.transactions,
        transactionAmounts: currentSession.transactions.map(t => ({
          type: t.type,
          amount: t.amount,
          amountType: typeof t.amount
        }))
      });
    }
  }, [currentSession, sessionResponse]);


  // Cálculos
  const calculateTotals = () => {
    if (!currentSession) return { cashSales: 0, currentBalance: 0, expectedBalance: 0, totalTransactions: 0 };

    // Filter out OPENING transactions from all calculations
    const nonOpeningTransactions = currentSession.transactions.filter(t => t.type !== 'OPENING');
    
    const cashSales = nonOpeningTransactions
      .filter(t => t.type === 'SALE' && t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);

    const totalTransactions = nonOpeningTransactions
      .reduce((sum, t) => sum + t.amount, 0);

    // The current balance is opening balance plus all transactions (except OPENING)
    const currentBalance = currentSession.openingBalance + totalTransactions;
    const expectedBalance = currentBalance;

    // Debug logging to help verify calculations
    console.log('🔍 CASH DRAWER DEBUG:', {
      sessionId: currentSession.id,
      openingBalance: currentSession.openingBalance,
      openingBalanceType: typeof currentSession.openingBalance,
      totalTransactions,
      totalTransactionsType: typeof totalTransactions,
      allTransactions: currentSession.transactions,
      nonOpeningTransactions,
      openingTransactions: currentSession.transactions.filter(t => t.type === 'OPENING'),
      cashSales,
      currentBalance,
      expectedBalance,
      calculation: `${currentSession.openingBalance} + ${totalTransactions} = ${currentBalance}`
    });

    return { cashSales, currentBalance, expectedBalance, totalTransactions };
  };

  const calculateCashCountTotal = () => {
    return (
      cashCount.bills_500 * 500 +
      cashCount.bills_100 * 100 +
      cashCount.bills_50 * 50 +
      cashCount.bills_20 * 20 +
      cashCount.bills_10 * 10 +
      cashCount.bills_5 * 5 +
      cashCount.bills_1 * 1 +
      cashCount.coins_050 * 0.50 +
      cashCount.coins_025 * 0.25 +
      cashCount.coins_010 * 0.10 +
      cashCount.coins_005 * 0.05
    );
  };

  const { cashSales, currentBalance, expectedBalance } = calculateTotals();
  const countedBalance = calculateCashCountTotal();
  const difference = countedBalance - expectedBalance;

  const handleOpenDrawer = async () => {
    if (!userLocationId) return;

    try {
      await openCashDrawer({
        locationId: userLocationId,
        openingBalance: parseFloat(openingBalance) || 0,
      }).unwrap();
      
      setOpenDrawerDialog(false);
      setOpeningBalance('');
    } catch (error) {
      console.error('Error al abrir caja:', error);
    }
  };

  const handleCloseDrawer = async () => {
    if (!currentSession) return;

    try {
      await closeCashDrawer({
        sessionId: currentSession.id,
        data: {
          closingBalance: countedBalance,
          notes: difference !== 0 ? `Diferencia de L. ${difference.toFixed(2)}` : undefined,
        },
      }).unwrap();
      
      setCloseDrawerDialog(false);
      setClosingStep(0);
      // Reset cash count
      setCashCount({
        bills_500: 0,
        bills_100: 0,
        bills_50: 0,
        bills_20: 0,
        bills_10: 0,
        bills_5: 0,
        bills_1: 0,
        coins_050: 0,
        coins_025: 0,
        coins_010: 0,
        coins_005: 0,
      });
    } catch (error) {
      console.error('Error al cerrar caja:', error);
    }
  };

  const handleCashAdjustment = async () => {
    const amount = parseFloat(adjustmentAmount);
    if (!currentSession || !amount || isNaN(amount)) return;

    try {
      await createAdjustment({
        sessionId: currentSession.id,
        data: {
          amount: amount,
          description: adjustmentReason,
        },
      }).unwrap();
      
      setAdjustmentDialog(false);
      setAdjustmentAmount('');
      setAdjustmentReason('');
    } catch (error) {
      console.error('Error al crear ajuste:', error);
    }
  };

  const handleWithdrawal = async () => {
    const amount = parseFloat(withdrawalAmount);
    if (!currentSession || !amount || amount <= 0 || isNaN(amount)) return;

    try {
      await createWithdrawal({
        sessionId: currentSession.id,
        data: {
          amount: amount,
          description: withdrawalReason,
        },
      }).unwrap();
      
      setWithdrawalDialog(false);
      setWithdrawalAmount('');
      setWithdrawalReason('');
    } catch (error) {
      console.error('Error al crear retiro:', error);
    }
  };

  const printCashDrawerReport = () => {
    console.log('Imprimiendo reporte de caja...');
    alert('📄 Reporte de caja impreso');
  };

  const handleViewSessionDetails = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setShowSessionDetailsDialog(true);
  };

  const handleCloseSessionDetails = () => {
    setShowSessionDetailsDialog(false);
    setSelectedSessionId('');
  };

  const closingSteps = [
    'Contar Efectivo',
    'Verificar Transacciones',
    'Confirmar Cierre'
  ];

  // Handle error state
  if (error) {
    return (
      <Box>
        <Alert severity="error">
          Error al cargar la sesión de caja. Verifica tu conexión e intenta nuevamente.
        </Alert>
      </Box>
    );
  }

  // Handle case where user has no location assigned
  if (!userLocationId) {
    const locations = locationsResponse?.data || [];
    const isAdmin = user?.role === 'ADMIN';
    
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Caja Registradora
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            No tienes una ubicación asignada
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Para usar la caja registradora necesitas tener una ubicación asignada. 
            {isAdmin 
              ? ' Como administrador, puedes asignarte una ubicación desde la página de usuarios.'
              : ' Contacta al administrador para que te asigne una ubicación.'
            }
          </Typography>
          
          {locations.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Ubicaciones disponibles:
              </Typography>
              {locations.map((location) => (
                <Chip 
                  key={location.id} 
                  label={location.name} 
                  size="small" 
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          )}
          
          {isAdmin && (
            <Box sx={{ mt: 2 }}>
              <Button 
                component={Link} 
                to="/users" 
                variant="contained" 
                size="small"
              >
                Ir a Gestión de Usuarios
              </Button>
            </Box>
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Encabezado */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Caja Registradora
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión y control de efectivo
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {!currentSession || currentSession.status === 'CLOSED' ? (
            <Button
              variant="contained"
              startIcon={<OpenIcon />}
              onClick={() => setOpenDrawerDialog(true)}
              size="large"
              disabled={isLoading}
            >
              {isLoading ? 'Cargando...' : 'Abrir Caja'}
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<MoneyIcon />}
                onClick={() => setAdjustmentDialog(true)}
              >
                Ajuste
              </Button>
              <Button
                variant="outlined"
                startIcon={<WalletIcon />}
                onClick={() => setWithdrawalDialog(true)}
              >
                Retiro
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<LockIcon />}
                onClick={() => setCloseDrawerDialog(true)}
              >
                Cerrar Caja
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Sesión Actual" icon={<CashIcon />} />
          {isAdmin && <Tab label="Historial" icon={<HistoryIcon />} />}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
      {/* Estado actual */}
      {currentSession && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CashIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Sesión de Caja
                    </Typography>
                    <Chip
                      label={currentSession.status === 'OPEN' ? 'ABIERTA' : 'CERRADA'}
                      color={currentSession.status === 'OPEN' ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </Box>
                  <Button
                    startIcon={<PrintIcon />}
                    onClick={printCashDrawerReport}
                  >
                    Imprimir Reporte
                  </Button>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Fecha: <strong>{new Date(currentSession.openTime).toLocaleDateString()}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hora de apertura: <strong>{new Date(currentSession.openTime).toLocaleTimeString()}</strong>
                    </Typography>
                    {currentSession.closeTime && (
                      <Typography variant="body2" color="text.secondary">
                        Hora de cierre: <strong>{new Date(currentSession.closeTime).toLocaleTimeString()}</strong>
                      </Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Usuario: <strong>{currentSession.user.name}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ubicación: <strong>{currentSession.location.name}</strong>
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {isAdmin && (
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Balance Actual
                  </Typography>
                  <Typography variant="h3" color="primary" fontWeight="bold">
                    {formatCurrency(currentBalance)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ventas en efectivo: {formatCurrency(cashSales)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Resumen financiero - Solo para admins */}
      {currentSession && isAdmin && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MoneyIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">Balance Inicial</Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(currentSession.openingBalance)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DollarIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">Ventas Efectivo</Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(cashSales)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CashIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">Balance Esperado</Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(expectedBalance)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckIcon color={difference >= 0 ? 'success' : 'error'} sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">Diferencia</Typography>
                </Box>
                <Typography 
                  variant="h5" 
                  fontWeight="bold"
                  color={difference >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(Math.abs(difference))}
                </Typography>
                {difference !== 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {difference > 0 ? 'Sobrante' : 'Faltante'}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Transacciones - Solo para admins */}
      {currentSession && isAdmin && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Transacciones del Día
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Hora</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Método</TableCell>
                  <TableCell align="right">Monto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentSession.transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>{new Date(transaction.createdAt).toLocaleTimeString()}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          transaction.type === 'SALE' ? 'Venta' :
                          transaction.type === 'ADJUSTMENT' ? 'Ajuste' :
                          transaction.type === 'WITHDRAWAL' ? 'Retiro' : 
                          transaction.type === 'DEPOSIT' ? 'Depósito' :
                          transaction.type === 'OPENING' ? 'Apertura' : 'Cierre'
                        }
                        size="small"
                        color={
                          transaction.type === 'SALE' ? 'success' :
                          transaction.type === 'ADJUSTMENT' ? 'info' :
                          transaction.type === 'WITHDRAWAL' ? 'warning' :
                          transaction.type === 'DEPOSIT' ? 'primary' : 'default'
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{transaction.description}</TableCell>
                    <TableCell>
                      {transaction.type === 'SALE' && transaction.sale ? '💵 Efectivo' : '💰 Caja'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={transaction.amount >= 0 ? 'success.main' : 'error.main'}
                        fontWeight="medium"
                      >
                        {transaction.amount >= 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Vista simplificada para no-admins */}
      {currentSession && !isAdmin && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Estado de la Caja
          </Typography>
          <Typography variant="body1" color="text.secondary">
            La caja está {currentSession.status === 'OPEN' ? 'abierta' : 'cerrada'} y funcionando correctamente.
            Puedes realizar las operaciones básicas usando los botones de arriba.
          </Typography>
        </Paper>
      )}

      {/* Dialog: Abrir Caja */}
      <Dialog open={openDrawerDialog} onClose={() => setOpenDrawerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OpenIcon />
            Abrir Caja Registradora
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Ingresa el monto inicial de efectivo en la caja registradora
          </Alert>
          
          <TextField
            fullWidth
            label="Balance Inicial"
            type="text"
            value={openingBalance}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9.]/g, '');
              setOpeningBalance(value);
            }}
            InputProps={{
              startAdornment: 'L. ',
            }}
            sx={{ mb: 2 }}
            helperText="Ingrese el monto inicial de efectivo"
          />
          
          <Typography variant="body2" color="text.secondary">
            Este será el monto de efectivo disponible al inicio del día. 
            Asegúrate de contar el efectivo antes de abrir la caja.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDrawerDialog(false)}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleOpenDrawer}
            disabled={isOpening}
          >
            {isOpening ? 'Abriendo...' : 'Abrir Caja'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Cerrar Caja */}
      <Dialog open={closeDrawerDialog} onClose={() => setCloseDrawerDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon />
            Cerrar Caja Registradora
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={closingStep} sx={{ mb: 3 }}>
            {closingSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {closingStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Conteo de Efectivo
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Cuenta el efectivo en la caja registradora y registra las cantidades:
              </Typography>
              
              <Grid container spacing={2}>
                {/* Billetes */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    Billetes
                  </Typography>
                  {[
                    { denomination: 500, key: 'bills_500' as keyof CashCount },
                    { denomination: 100, key: 'bills_100' as keyof CashCount },
                    { denomination: 50, key: 'bills_50' as keyof CashCount },
                    { denomination: 20, key: 'bills_20' as keyof CashCount },
                    { denomination: 10, key: 'bills_10' as keyof CashCount },
                    { denomination: 5, key: 'bills_5' as keyof CashCount },
                    { denomination: 1, key: 'bills_1' as keyof CashCount },
                  ].map(({ denomination, key }) => (
                    <Grid container spacing={2} key={key} sx={{ mb: 1 }}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2">L. {denomination}</Typography>
                      </Grid>
                      <Grid size={{ xs: 3 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={cashCount[key] === 0 ? '' : cashCount[key]}
                          onChange={(e) => setCashCount({
                            ...cashCount,
                            [key]: parseInt(e.target.value) || 0
                          })}
                          inputProps={{ min: 0 }}
                          placeholder="0"
                        />
                      </Grid>
                      <Grid size={{ xs: 3 }}>
                        <Typography variant="body2">
                          {formatCurrency(denomination * (cashCount[key] as number))}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                
                {/* Monedas */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    Monedas
                  </Typography>
                  {[
                    { denomination: 0.50, key: 'coins_050' as keyof CashCount, label: '50 centavos' },
                    { denomination: 0.25, key: 'coins_025' as keyof CashCount, label: '25 centavos' },
                    { denomination: 0.10, key: 'coins_010' as keyof CashCount, label: '10 centavos' },
                    { denomination: 0.05, key: 'coins_005' as keyof CashCount, label: '5 centavos' },
                  ].map(({ denomination, key, label }) => (
                    <Grid container spacing={2} key={key} sx={{ mb: 1 }}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2">{label}</Typography>
                      </Grid>
                      <Grid size={{ xs: 3 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={cashCount[key] === 0 ? '' : cashCount[key]}
                          onChange={(e) => setCashCount({
                            ...cashCount,
                            [key]: parseInt(e.target.value) || 0
                          })}
                          inputProps={{ min: 0 }}
                          placeholder="0"
                        />
                      </Grid>
                      <Grid size={{ xs: 3 }}>
                        <Typography variant="body2">
                          {formatCurrency(denomination * (cashCount[key] as number))}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">
                  Total Contado: {formatCurrency(countedBalance)}
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => setClosingStep(1)}
                  disabled={countedBalance === 0}
                >
                  Continuar
                </Button>
              </Box>
            </Box>
          )}

          {closingStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Verificación de Transacciones
              </Typography>
              
              {isAdmin && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Balance Inicial
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {formatCurrency(currentSession?.openingBalance || 0)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Ventas en Efectivo
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {formatCurrency(cashSales)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Balance Esperado
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {formatCurrency(expectedBalance)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Balance Contado
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {formatCurrency(countedBalance)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {isAdmin && difference !== 0 && (
                <Alert 
                  severity={Math.abs(difference) <= 5 ? 'warning' : 'error'} 
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle2">
                    {difference > 0 ? 'Sobrante' : 'Faltante'} detectado: {formatCurrency(Math.abs(difference))}
                  </Typography>
                  <Typography variant="body2">
                    {Math.abs(difference) <= 5 
                      ? 'Diferencia menor, dentro del rango aceptable.'
                      : 'Diferencia significativa, verifica el conteo y las transacciones.'
                    }
                  </Typography>
                </Alert>
              )}
              
              {!isAdmin && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Asegúrate de contar correctamente el efectivo antes de continuar.
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={() => setClosingStep(0)}>
                  Volver
                </Button>
                <Button 
                  variant="contained" 
                  onClick={() => setClosingStep(2)}
                >
                  Continuar
                </Button>
              </Box>
            </Box>
          )}

          {closingStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Confirmar Cierre de Caja
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                Una vez cerrada la caja, no podrás realizar más transacciones hasta la próxima apertura.
              </Alert>
              
              {isAdmin && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Balance Inicial:</strong></TableCell>
                        <TableCell align="right">{formatCurrency(currentSession?.openingBalance || 0)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Ventas en Efectivo:</strong></TableCell>
                        <TableCell align="right">{formatCurrency(cashSales)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Balance Esperado:</strong></TableCell>
                        <TableCell align="right">{formatCurrency(expectedBalance)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Balance Contado:</strong></TableCell>
                        <TableCell align="right">{formatCurrency(countedBalance)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Diferencia:</strong></TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ color: difference >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}
                        >
                          {difference >= 0 ? '+' : ''}{formatCurrency(difference)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {!isAdmin && (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                  El efectivo ha sido contado correctamente. 
                  <br />
                  Total contado: <strong>{formatCurrency(countedBalance)}</strong>
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button onClick={() => setClosingStep(1)}>
                  Volver
                </Button>
                <Button 
                  variant="contained" 
                  color="error"
                  onClick={handleCloseDrawer}
                  startIcon={<LockIcon />}
                  disabled={isClosing}
                >
                  {isClosing ? 'Cerrando...' : 'Cerrar Caja'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Ajuste de Caja */}
      <Dialog open={adjustmentDialog} onClose={() => setAdjustmentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajuste de Caja</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Los ajustes de caja deben tener una justificación válida y serán registrados en el reporte.
          </Alert>
          
          <TextField
            fullWidth
            label="Monto del Ajuste"
            type="text"
            value={adjustmentAmount}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9.-]/g, '');
              setAdjustmentAmount(value);
            }}
            InputProps={{
              startAdornment: 'L. ',
            }}
            sx={{ mb: 2 }}
            helperText="Ingresa un número positivo para agregar dinero, negativo para restar"
          />
          
          <TextField
            fullWidth
            label="Motivo del Ajuste"
            multiline
            rows={3}
            value={adjustmentReason}
            onChange={(e) => setAdjustmentReason(e.target.value)}
            placeholder="Explica el motivo del ajuste..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustmentDialog(false)}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCashAdjustment}
            disabled={!adjustmentReason.trim() || !adjustmentAmount.trim() || isNaN(parseFloat(adjustmentAmount)) || parseFloat(adjustmentAmount) === 0 || isCreatingAdjustment}
          >
            {isCreatingAdjustment ? 'Aplicando...' : 'Aplicar Ajuste'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Retiro de Efectivo */}
      <Dialog open={withdrawalDialog} onClose={() => setWithdrawalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Retiro de Efectivo</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Registra retiros de efectivo de la caja (cambio, gastos menores, etc.)
          </Alert>
          
          <TextField
            fullWidth
            label="Monto del Retiro"
            type="text"
            value={withdrawalAmount}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9.]/g, '');
              setWithdrawalAmount(value);
            }}
            InputProps={{
              startAdornment: 'L. ',
            }}
            sx={{ mb: 2 }}
            inputProps={{ min: 0 }}
          />
          
          <TextField
            fullWidth
            label="Motivo del Retiro"
            multiline
            rows={3}
            value={withdrawalReason}
            onChange={(e) => setWithdrawalReason(e.target.value)}
            placeholder="Explica el motivo del retiro..."
            required
          />
          
          {isAdmin && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Balance disponible: {formatCurrency(currentBalance)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawalDialog(false)}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleWithdrawal}
            disabled={!withdrawalReason.trim() || !withdrawalAmount.trim() || isNaN(parseFloat(withdrawalAmount)) || parseFloat(withdrawalAmount) <= 0 || (isAdmin && parseFloat(withdrawalAmount) > currentBalance) || isCreatingWithdrawal}
          >
            {isCreatingWithdrawal ? 'Registrando...' : 'Registrar Retiro'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}

      {/* History Tab - Admin Only */}
      {isAdmin && activeTab === 1 && (
        <Box>
          {/* Filter Controls */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Filtros de Búsqueda
            </Typography>
            
            <Grid container spacing={3} alignItems="end">
              {/* User Filter */}
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Usuario</InputLabel>
                  <Select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    label="Usuario"
                    startAdornment={<PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                  >
                    <MenuItem value="">
                      <em>Todos los usuarios</em>
                    </MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Location Filter (Admin only) */}
              {isAdmin && (
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Ubicación</InputLabel>
                    <Select
                      value={selectedLocationId}
                      onChange={(e) => setSelectedLocationId(e.target.value)}
                      label="Ubicación"
                      startAdornment={<LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                    >
                      <MenuItem value="">
                        <em>Mi ubicación</em>
                      </MenuItem>
                      {locations.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Payment Method Filter */}
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Método de Pago</InputLabel>
                  <Select
                    value={selectedPaymentMethod}
                    onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    label="Método de Pago"
                    startAdornment={<CardIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                  >
                    <MenuItem value="">
                      <em>Todos los métodos</em>
                    </MenuItem>
                    <MenuItem value="CASH">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: 'success.main', borderRadius: '50%' }} />
                        Efectivo
                      </Box>
                    </MenuItem>
                    <MenuItem value="CARD">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: 'info.main', borderRadius: '50%' }} />
                        Tarjeta
                      </Box>
                    </MenuItem>
                    <MenuItem value="TRANSFER">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: 'warning.main', borderRadius: '50%' }} />
                        Transferencia
                      </Box>
                    </MenuItem>
                    <MenuItem value="CHECK">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: 'secondary.main', borderRadius: '50%' }} />
                        Cheque
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Date Filters */}
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  label="Fecha Inicio"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  label="Fecha Fin"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Clear Filters Button */}
              <Grid size={{ xs: 12, md: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setSelectedUserId('');
                    setSelectedLocationId('');
                    setSelectedPaymentMethod('');
                    setStartDate('');
                    setEndDate('');
                    setHistoryPage(1);
                  }}
                >
                  Limpiar Filtros
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Session History */}
          <Paper>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Historial de Sesiones
              </Typography>
              {isLoadingHistory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <Typography>Cargando historial...</Typography>
                </Box>
              ) : sessionHistory.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                  <Typography color="text.secondary">
                    No se encontraron sesiones con los filtros aplicados
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Usuario</TableCell>
                          <TableCell>Ubicación</TableCell>
                          <TableCell>Fecha Apertura</TableCell>
                          <TableCell>Fecha Cierre</TableCell>
                          <TableCell>Balance Inicial</TableCell>
                          <TableCell>Balance Final</TableCell>
                          <TableCell>Efectivo</TableCell>
                          <TableCell>Tarjeta</TableCell>
                          <TableCell>Transferencia</TableCell>
                          <TableCell>Cheque</TableCell>
                          <TableCell>Total Ventas</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell align="center">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sessionHistory
                          .filter((session) => {
                            if (!selectedPaymentMethod) return true;

                            const paymentMethodTotal = session.paymentMethodTotals?.[selectedPaymentMethod as keyof typeof session.paymentMethodTotals] || 0;
                            return paymentMethodTotal > 0;
                          })
                          .map((session) => (
                          <TableRow key={session.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon fontSize="small" color="primary" />
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {session.user.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {session.user.email}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocationIcon fontSize="small" color="secondary" />
                                <Typography variant="body2">
                                  {session.location.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {new Date(session.openTime).toLocaleString('es-ES')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {session.closeTime ? 
                                  new Date(session.closeTime).toLocaleString('es-ES') : 
                                  'Aún abierta'
                                }
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {formatCurrency(session.openingBalance)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {session.closingBalance !== null && session.closingBalance !== undefined ? 
                                  formatCurrency(session.closingBalance) : 
                                  '-'
                                }
                              </Typography>
                            </TableCell>
                            {/* Payment Method Columns */}
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium" color="success.main">
                                {formatCurrency(session.paymentMethodTotals?.CASH || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium" color="info.main">
                                {formatCurrency(session.paymentMethodTotals?.CARD || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium" color="warning.main">
                                {formatCurrency(session.paymentMethodTotals?.TRANSFER || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium" color="secondary.main">
                                {formatCurrency(session.paymentMethodTotals?.CHECK || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {formatCurrency(session.paymentMethodTotals?.TOTAL || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={session.status === 'OPEN' ? 'Abierta' : 'Cerrada'}
                                size="small"
                                color={session.status === 'OPEN' ? 'success' : 'default'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleViewSessionDetails(session.id)}
                              >
                                Ver Detalles
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  {historyPagination && historyPagination.pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
                      <Button
                        disabled={historyPage === 1}
                        onClick={() => setHistoryPage(historyPage - 1)}
                      >
                        Anterior
                      </Button>
                      <Typography sx={{ mx: 2 }}>
                        Página {historyPage} de {historyPagination.pages}
                      </Typography>
                      <Button
                        disabled={historyPage === historyPagination.pages}
                        onClick={() => setHistoryPage(historyPage + 1)}
                      >
                        Siguiente
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Session Details Dialog */}
      <Dialog
        open={showSessionDetailsDialog}
        onClose={handleCloseSessionDetails}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VisibilityIcon />
              <Typography variant="h6">
                Detalles de Sesión
              </Typography>
            </Box>
            {sessionDetails && (
              <Chip
                label={sessionDetails.status === 'OPEN' ? 'Abierta' : 'Cerrada'}
                color={sessionDetails.status === 'OPEN' ? 'success' : 'default'}
                variant="outlined"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {isLoadingSessionDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : sessionDetails ? (
            <Box>
              {/* Session Summary */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Información de la Sesión
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Usuario:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        <PersonIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {sessionDetails.user.name}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Ubicación:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        <LocationIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {sessionDetails.location.name}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Fecha y Hora de Apertura:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {new Date(sessionDetails.openTime).toLocaleString('es-ES')}
                      </Typography>
                    </Box>
                    {sessionDetails.closeTime && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Fecha y Hora de Cierre:</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {new Date(sessionDetails.closeTime).toLocaleString('es-ES')}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>

              {/* Financial Summary */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Resumen Financiero
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Balance Inicial</Typography>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        {formatCurrency(sessionDetails.openingBalance)}
                      </Typography>
                    </Box>
                  </Grid>
                  {sessionDetails.closingBalance !== null && (
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Balance Final</Typography>
                        <Typography variant="h6" color="success.main" fontWeight="bold">
                          {formatCurrency(sessionDetails.closingBalance)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {sessionDetails.expectedBalance !== null && (
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Balance Esperado</Typography>
                        <Typography variant="h6" color="info.main" fontWeight="bold">
                          {formatCurrency(sessionDetails.expectedBalance)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {sessionDetails.difference !== null && (
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Diferencia</Typography>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color={sessionDetails.difference === 0 ? 'success.main' : sessionDetails.difference > 0 ? 'success.main' : 'error.main'}
                        >
                          {sessionDetails.difference >= 0 ? '+' : ''}{formatCurrency(sessionDetails.difference)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Detailed Transactions */}
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Transacciones Detalladas ({sessionDetails.transactions.length})
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fecha/Hora</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell>Detalles</TableCell>
                        <TableCell align="right">Monto</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sessionDetails.transactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(transaction.createdAt).toLocaleString('es-ES')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                transaction.type === 'SALE' ? 'Venta' :
                                transaction.type === 'ADJUSTMENT' ? 'Ajuste' :
                                transaction.type === 'WITHDRAWAL' ? 'Retiro' :
                                transaction.type === 'DEPOSIT' ? 'Depósito' :
                                transaction.type === 'OPENING' ? 'Apertura' : 'Cierre'
                              }
                              color={
                                transaction.type === 'SALE' ? 'success' :
                                transaction.type === 'ADJUSTMENT' ? 'info' :
                                transaction.type === 'WITHDRAWAL' ? 'warning' :
                                transaction.type === 'DEPOSIT' ? 'primary' : 'default'
                              }
                              icon={
                                transaction.type === 'SALE' ? <ShoppingCartIcon /> :
                                transaction.type === 'ADJUSTMENT' ? <TransferIcon /> :
                                transaction.type === 'WITHDRAWAL' ? <RemoveIcon /> :
                                transaction.type === 'DEPOSIT' ? <AddIcon /> :
                                transaction.type === 'OPENING' ? <OpenIcon /> : <LockIcon />
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {transaction.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {transaction.sale ? (
                              <Box>
                                <Typography variant="body2">
                                  <strong>Venta #{transaction.sale.id.slice(-8)}</strong>
                                </Typography>
                                {transaction.sale.customerName && (
                                  <Typography variant="caption" color="text.secondary">
                                    Cliente: {transaction.sale.customerName}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Total: {formatCurrency(transaction.sale.total)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {transaction.sale.paymentMethod}
                                </Typography>
                                {transaction.sale.saleItems && transaction.sale.saleItems.length > 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" fontWeight="bold">
                                      Productos:
                                    </Typography>
                                    {transaction.sale.saleItems.slice(0, 3).map((item, idx) => (
                                      <Typography key={idx} variant="caption" display="block" color="text.secondary">
                                        • {item.product?.name || 'Producto'} x{item.quantity}
                                      </Typography>
                                    ))}
                                    {transaction.sale.saleItems.length > 3 && (
                                      <Typography variant="caption" color="text.secondary">
                                        +{transaction.sale.saleItems.length - 3} productos más...
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Sin detalles adicionales
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color={transaction.amount >= 0 ? 'success.main' : 'error.main'}
                            >
                              {transaction.amount >= 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          ) : (
            <Alert severity="error">
              Error al cargar los detalles de la sesión
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSessionDetails}>
            Cerrar
          </Button>
          {sessionDetails && (
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => {
                console.log('Imprimiendo reporte detallado de sesión:', sessionDetails.id);
                alert('📄 Reporte detallado de sesión impreso');
              }}
            >
              Imprimir Reporte
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CashDrawerPage;